import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, ExternalLink, Pin, Lock } from "lucide-react";

export default function NewsAdmin() {
  const { user } = useAuth();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);

  const [form, setForm] = useState({
    title: "",
    url: "",
    summary: "",
    source: "Resmo Picks",
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/news/picks/all");
      setPicks(data || []);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) {
      setError("Title and URL are required.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      await api.post("/news/picks", form);
      setForm({ title: "", url: "", summary: "", source: "Resmo Picks" });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pick?")) return;
    try {
      await api.delete(`/news/picks/${id}`);
      setPicks((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center">
          <Lock size={48} className="mx-auto mb-3" />
          <h1 className="font-display text-3xl uppercase">Admin only.</h1>
          <p className="font-mono-rm text-sm mt-2">
            The News Editor is reserved for Resmo admins.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 pb-20">
        <div className="rm-tag mb-3">Admin · News editor</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">
          Editor's <span className="rm-text-orange">picks.</span>
        </h1>
        <p className="text-base md:text-lg max-w-2xl mb-8" style={{ color: "#475569" }}>
          Pin up to 5 hand-picked stories to the top of every user's Research &amp; Dev news feed.
          Signed in as: <strong>{user?.email}</strong>
        </p>

        {error && (
          <div className="rm-card-static p-3 mb-6 font-mono-rm text-sm" style={{ background: "#FEF2F2", borderColor: "#EF4444", color: "#991B1B" }}>
            {error}
          </div>
        )}

        <form onSubmit={handleCreate} className="rm-card-static p-6 md:p-7 mb-8 space-y-4">
          <div className="rm-label">Add a pick</div>
          <input
            type="text"
            placeholder="Headline"
            className="rm-input w-full"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="pick-title-input"
            required
          />
          <input
            type="url"
            placeholder="https://…"
            className="rm-input w-full"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            data-testid="pick-url-input"
            required
          />
          <textarea
            placeholder="One-sentence summary (optional)"
            className="rm-input w-full"
            rows={2}
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            data-testid="pick-summary-input"
            maxLength={500}
          />
          <input
            type="text"
            placeholder="Source label (e.g. 'Resmo Picks', 'Bloomberg')"
            className="rm-input w-full"
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            data-testid="pick-source-input"
            maxLength={80}
          />
          <button
            type="submit"
            disabled={creating}
            className="rm-btn rm-btn-primary"
            data-testid="pick-create-btn"
          >
            <Plus size={16} /> {creating ? "Pinning…" : "Pin to feed"}
          </button>
        </form>

        <div className="rm-label mb-3">All picks · {picks.length}</div>
        {loading ? (
          <div className="font-mono-rm text-sm text-center py-8">Loading…</div>
        ) : picks.length === 0 ? (
          <div className="rm-card-static p-6 text-center" data-testid="picks-empty">
            <div className="font-display text-xl uppercase">No picks yet.</div>
            <p className="font-mono-rm text-sm mt-2" style={{ color: "#475569" }}>
              Add your first pick above — it'll appear at the top of every user's Research &amp; Dev page.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="picks-list">
            {picks.map((p) => (
              <div key={p.id} className="rm-card-static p-4 flex items-start gap-3" data-testid={`pick-${p.id}`}>
                <Pin size={14} className="flex-shrink-0 mt-1 rm-text-orange" />
                <div className="flex-1 min-w-0">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-sm hover:underline inline-flex items-center gap-1"
                  >
                    {p.title} <ExternalLink size={11} />
                  </a>
                  {p.summary && (
                    <div className="text-xs mt-1" style={{ color: "#475569" }}>
                      {p.summary}
                    </div>
                  )}
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-2" style={{ color: "#475569" }}>
                    <span className="rm-text-orange font-bold">{p.source}</span>
                    {p.created_at && <> · {new Date(p.created_at).toLocaleString()}</>}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="rm-btn rm-btn-ghost text-xs p-2 flex-shrink-0"
                  data-testid={`delete-pick-${p.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
